'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';

// --- TIPOS (Mantidos iguais) ---
export type Voter = { identity: string; name?: string; };
export type PollOption = { id: string; text: string; voters: Voter[]; };
export type Poll = { id: string; createdAt: number; question: string; options: PollOption[]; isActive: boolean; };
type PollMessage = 
  | { type: 'POLL_CREATE'; payload: Poll }
  | { type: 'POLL_VOTE'; payload: { pollId: string; optionId: string; voter: Voter } }
  | { type: 'POLL_END'; payload: { pollId: string } }
  | { type: 'POLL_SYNC_REQ'; payload: null }
  | { type: 'POLL_SYNC_RES'; payload: Poll[] };

const TOPIC_POLL = 'lk-poll';

interface PollContextType {
  polls: Poll[];
  activePoll: Poll | undefined;
  hasVotedInActive: boolean;
  isHost: boolean;
  createPoll: (question: string, options: string[]) => void;
  vote: (pollId: string, optionId: string) => void;
  endPoll: (pollId: string) => void;
  togglePollModal: () => void;
  isPollModalOpen: boolean;
}

export const PollContext = createContext<PollContextType | undefined>(undefined);

export function PollProvider({ children, isHost = false }: { children: React.ReactNode; isHost?: boolean }) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  
  const [polls, setPolls] = useState<Poll[]>([]);
  const [isPollModalOpen, setIsPollModalOpen] = useState(false);

  const API_URL = 'https://meet-api.evolutto.com.br';

  const activePoll = useMemo(() => polls.find(p => p.isActive), [polls]);
  
  const hasVotedInActive = useMemo(() => {
    if (!activePoll || !localParticipant) return false;
    return activePoll.options.some(opt => 
      opt.voters.some(v => v.identity === localParticipant.identity)
    );
  }, [activePoll, localParticipant]);

  const encoder = new TextEncoder();
  const decoder = new TextDecoder();

  const sendData = useCallback((data: PollMessage) => {
    const strData = JSON.stringify(data);
    const payload = encoder.encode(strData);
    if (room.localParticipant) {
      room.localParticipant.publishData(payload, { reliable: true, topic: TOPIC_POLL });
    }
  }, [room, encoder]);

  // Recupera histórico local
  useEffect(() => {
    if (isHost && typeof window !== 'undefined' && room?.name) {
      const key = `evolutto-polls-${room.name}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setPolls(parsed.map((p: Poll) => ({ ...p, isActive: false })));
        } catch (e) {}
      }
    }
  }, [isHost, room?.name]);

  useEffect(() => {
    if (isHost && polls.length > 0 && room?.name) {
      localStorage.setItem(`evolutto-polls-${room.name}`, JSON.stringify(polls));
    }
  }, [polls, isHost, room?.name]);

  // --- FUNÇÕES DE API ---

  const savePollToBackend = async (poll: Poll) => {
    console.log('🚀 EVOLU DEBUG: Tentando salvar enquete...', poll);

    if (!API_URL) {
      console.error('❌ EVOLU DEBUG: API_URL vazia. Abortando fetch.');
      return;
    }

    try {
      const url = `${API_URL}/api/polls`;
      console.log('🌐 EVOLU DEBUG: Fetching:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_name: room.name,
          question: poll.question,
          options: poll.options.map(o => o.text),
          poll_id: poll.id,
          is_anonymous: false,
          is_active: true
        })
      });

      console.log('✅ EVOLU DEBUG: Status resposta:', response.status);
      
      if (!response.ok) {
        const text = await response.text();
        console.error('❌ EVOLU DEBUG: Erro Body:', text);
      }
    } catch (error) {
      console.error("❌ EVOLU DEBUG: Exception no fetch:", error);
    }
  };

  const saveVoteToBackend = async (pollId: string, optionId: string, optionText: string) => {
    if (!API_URL) return;
    try {
      await fetch(`${API_URL}/api/polls/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          poll_id: pollId, 
          option_id: optionId, 
          option_text: optionText,
          participant_identity: localParticipant.identity,
          participant_name: localParticipant.name || 'Anônimo'
        })
      });
    } catch (error) {
      console.error("Erro ao salvar voto", error);
    }
  };

  // --- AÇÕES ---

  const createPoll = (question: string, optionsTexts: string[]) => {
    console.log('⚡ EVOLU DEBUG: createPoll acionado.');
    
    if (activePoll) endPoll(activePoll.id);

    const newPoll: Poll = {
      id: Math.random().toString(36).substr(2, 9),
      createdAt: Date.now(),
      question,
      options: optionsTexts.map(text => ({ id: Math.random().toString(36).substr(2, 9), text, voters: [] })),
      isActive: true,
    };
    
    setPolls(prev => [newPoll, ...prev]);
    sendData({ type: 'POLL_CREATE', payload: newPoll });
    setIsPollModalOpen(false);

    // --- LOGICA DE DISPARO ---
    if (isHost) {
      console.log('👤 EVOLU DEBUG: Sou host, chamando savePollToBackend.');
      savePollToBackend(newPoll);
    } else {
      console.warn('⚠️ EVOLU DEBUG: createPoll chamado mas isHost é false. Backend não será notificado.');
    }
  };

  const vote = (pollId: string, optionId: string) => {
    const targetPoll = polls.find(p => p.id === pollId);
    if (!targetPoll || !targetPoll.isActive) return;

    // Check voto duplicado
    if (targetPoll.options.some(opt => opt.voters.some(v => v.identity === localParticipant.identity))) return;

    const voter: Voter = {
      identity: localParticipant.identity,
      name: localParticipant.name || localParticipant.identity,
    };

    const selectedOption = targetPoll.options.find(o => o.id === optionId);
    
    setPolls(prev => prev.map(p => {
      if (p.id !== pollId) return p;
      return {
        ...p,
        options: p.options.map(opt => 
          opt.id === optionId ? { ...opt, voters: [...opt.voters, voter] } : opt
        )
      };
    }));

    sendData({ type: 'POLL_VOTE', payload: { pollId, optionId, voter } });
    saveVoteToBackend(pollId, optionId, selectedOption?.text || '');
  };

  const endPoll = (pollId: string) => {
    setPolls(prev => prev.map(p => p.id === pollId ? { ...p, isActive: false } : p));
    sendData({ type: 'POLL_END', payload: { pollId } });
  };

  // Listeners
  useEffect(() => {
    if (!isHost) {
      const timeout = setTimeout(() => sendData({ type: 'POLL_SYNC_REQ', payload: null }), 1000);
      return () => clearTimeout(timeout);
    }
  }, [isHost, sendData]);

  useEffect(() => {
    if (!room) return;
    const handleData = (payload: Uint8Array, participant: any, _kind: any, topic?: string) => {
      if (topic !== TOPIC_POLL) return;
      try {
        const message = JSON.parse(decoder.decode(payload)) as PollMessage;
        switch (message.type) {
          case 'POLL_CREATE': setPolls(prev => [message.payload, ...prev]); break;
          case 'POLL_VOTE': 
            setPolls(prev => prev.map(p => {
              if (p.id !== message.payload.pollId) return p;
              // Lógica de atualização de votos
              const optIndex = p.options.findIndex(o => o.id === message.payload.optionId);
              if (optIndex === -1 || p.options[optIndex].voters.some(v => v.identity === message.payload.voter.identity)) return p;
              return {
                ...p,
                options: p.options.map(opt => opt.id === message.payload.optionId ? { ...opt, voters: [...opt.voters, message.payload.voter] } : opt)
              };
            }));
            break;
          case 'POLL_END': setPolls(prev => prev.map(p => p.id === message.payload.pollId ? { ...p, isActive: false } : p)); break;
          case 'POLL_SYNC_REQ': if (isHost) sendData({ type: 'POLL_SYNC_RES', payload: polls }); break;
          case 'POLL_SYNC_RES': if (!isHost) setPolls(message.payload); break;
        }
      } catch (error) { console.error(error); }
    };
    room.on(RoomEvent.DataReceived, handleData);
    return () => { room.off(RoomEvent.DataReceived, handleData); };
  }, [room, polls, isHost, sendData]);

  return (
    <PollContext.Provider value={{ polls, activePoll, hasVotedInActive, isHost, createPoll, vote, endPoll, togglePollModal: () => setIsPollModalOpen(!isPollModalOpen), isPollModalOpen }}>
      {children}
    </PollContext.Provider>
  );
}

export const usePoll = () => {
  const context = useContext(PollContext);
  if (!context) throw new Error('usePoll must be used within a PollProvider');
  return context;
};